import { Skeleton } from '@mui/material';
import { MetricCard } from '@fragments';
import {
    IconCoin,
    IconReceiptRefund,
    IconShieldDollar,
    IconReceipt,
    IconCalculator,
    IconAlertTriangle,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import './styles.scss';

const TILE_COUNT = 6;

function toMajor(cents) {
    if (cents == null) return 0;
    return Math.round(Number(cents)) / 100;
}

function sentimentByThreshold(percentile, green, red) {
    const p = Number(percentile) || 0;
    if (p < green) return 'positive';
    if (p >= red) return 'negative';
    return 'warning';
}

function MetricsSkeleton() {
    return Array.from({ length: TILE_COUNT }, (_, i) => (
        <Skeleton variant="rounded" height={112} className="insights-metrics__skeleton" key={i} />
    ));
}

// KPI tiles for the selected currency, beside the revenue hero (which owns the
// gross revenue figure, so it isn't repeated here). `summary` is null-safe — a
// currency with no charges renders zeros rather than blanking.
export default function MetricsSection({ report, currency, reportLoading }) {
    const { t } = useTranslation('dashboard');

    const currencyReport = report?.currencyReports?.find(r => r.currency === currency) || null;
    const summary = currencyReport?.summary || null;
    const revenue = summary?.revenue;

    // Per-bucket sales counts over the period → the "Total de Vendas" sparkline.
    const salesTrend = (currencyReport?.salesAmount?.data || []).map(p => Number(p.value) || 0);

    const firstLoad = reportLoading && !report;
    const reloading = reportLoading && !!report;

    return (
        <div className={`insights-metrics${reloading ? ' insights-metrics--reloading' : ''}`}>
            {firstLoad ? (
                <MetricsSkeleton />
            ) : (
                <>
                    <MetricCard
                        title={t('insights.metrics.salesTotal.title')}
                        leftIcon={<IconReceipt size={15} />}
                        value={summary?.salesTotal ?? 0}
                        variant="number"
                        chartValues={salesTrend}
                        description={t('insights.metrics.salesTotal.description')}
                        loading={reportLoading}
                        cornerRadius="large"
                    />
                    <MetricCard
                        title={t('insights.metrics.netRevenue.title')}
                        leftIcon={<IconCoin size={15} />}
                        value={toMajor(revenue?.liquid)}
                        variant="money"
                        currency={currency}
                        description={t('insights.metrics.netRevenue.description')}
                        loading={reportLoading}
                        cornerRadius="large"
                    />
                    <MetricCard
                        title={t('insights.metrics.commission.title')}
                        leftIcon={<IconShieldDollar size={15} />}
                        value={toMajor(revenue?.commission)}
                        variant="money"
                        currency={currency}
                        description={t('insights.metrics.commission.description')}
                        loading={reportLoading}
                        cornerRadius="large"
                    />
                    <MetricCard
                        title={t('insights.metrics.averageTicket.title')}
                        leftIcon={<IconCalculator size={15} />}
                        value={toMajor(revenue?.averageTicket)}
                        variant="money"
                        currency={currency}
                        description={t('insights.metrics.averageTicket.description')}
                        loading={reportLoading}
                        cornerRadius="large"
                    />
                    <MetricCard
                        title={t('insights.metrics.refunds.title')}
                        leftIcon={<IconReceiptRefund size={15} />}
                        value={toMajor(summary?.refund?.amount)}
                        variant="money"
                        currency={currency}
                        note={t('insights.metrics.shareOfSales', { value: Math.round((summary?.refund?.percentile ?? 0) * 10) / 10 })}
                        noteSentiment={sentimentByThreshold(summary?.refund?.percentile, 4.5, 6)}
                        description={t('insights.metrics.refunds.description')}
                        loading={reportLoading}
                        cornerRadius="large"
                    />
                    <MetricCard
                        title={t('insights.metrics.chargebacks.title')}
                        leftIcon={<IconAlertTriangle size={15} />}
                        value={toMajor(summary?.chargeback?.amount)}
                        variant="money"
                        currency={currency}
                        note={t('insights.metrics.shareOfSales', { value: Math.round((summary?.chargeback?.percentile ?? 0) * 10) / 10 })}
                        noteSentiment={sentimentByThreshold(summary?.chargeback?.percentile, 1, 1.5)}
                        description={t('insights.metrics.chargebacks.description')}
                        loading={reportLoading}
                        cornerRadius="large"
                    />
                </>
            )}
        </div>
    );
}
